/**
 * TierMatrixEditor — Full privilege matrix: Screens (rows) × License Tiers (columns)
 *
 * LEFT PANE : tier selector list with per-tier stats + inline Save/All/None actions
 * RIGHT PANE: scrollable table
 *   - Rows   : Module  →  SubModule  →  Screen   (collapsible)
 *   - Columns: one per License Tier with vertical-text headers
 *   - Cells  : tri-state checkboxes  (all / partial / none)
 *
 * Selected tier column is highlighted; clicking another tier in left pane
 * shifts the highlight without hiding other columns.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Save, Search, Database, RefreshCw, ChevronDown, ChevronRight, Globe, Layers } from 'lucide-react';
import { erpToast } from './erp/ERPToast';
import '../styles/erp-theme.css';
import {
  TIER_ORDER, TIER_COLORS, TIER_SHORT,   // kept as fallback exports only
  COL_W, HDR_H,
  TCB, ModuleBlock
} from './TierMatrixBlocks';
import { useTierList } from '../lib/useTierList';
import { API, apiFetch } from '../lib/api';


// Re-export so existing importers (e.g. PricingConfiguration) continue to work
export { TIER_ORDER, TIER_COLORS };

/**
 * Compute a minimal diff between the baseline (DB-loaded) and current (user-edited)
 * enabled state. Only changed rows are returned — this is what gets sent to the API.
 * Payload size: O(changes) instead of O(total_rows).
 */
function computeDiff(baseline, current) {
  const changes = [];
  const pairs = [
    { key: 'modules',    entityType: 'Module'    },
    { key: 'subModules', entityType: 'SubModule' },
    { key: 'screens',    entityType: 'Screen'    },
  ];
  for (const { key, entityType } of pairs) {
    const bSet = baseline[key] || new Set();
    const cSet = current[key]  || new Set();
    // Was enabled, now disabled → send enabled:false
    for (const id of bSet) {
      if (!cSet.has(id)) changes.push({ entityType, entityId: id, enabled: false });
    }
    // Was disabled, now enabled → send enabled:true
    for (const id of cSet) {
      if (!bSet.has(id)) changes.push({ entityType, entityId: id, enabled: true });
    }
  }
  return changes;
}

/** Clone a tier's enabled Sets so baseline is isolated from future edits. */
function cloneTierSets(te) {
  return {
    modules:    new Set(te?.modules    || []),
    subModules: new Set(te?.subModules || []),
    screens:    new Set(te?.screens    || []),
  };
}


/* ── Main component ─────────────────────────────────────────────────────── */
export default function TierMatrixEditor() {
  /* ── Dynamic tier list (replaces hardcoded TIER_ORDER) ───────────────── */
  const { tiers: rawTiers, colors, shorts, loading: tiersLoading } = useTierList();
  // Stabilise the tiers reference — only recreate when the CONTENT changes, not on
  // every re-render of useTierList. Without this, setSaving() → re-render → new tiers
  // reference → loadAll useCallback recreated → useEffect fires loadAll mid-save → race.
  // eslint-disable-next-line
  const tiers = useMemo(() => rawTiers || [], [(rawTiers || []).join('|')]);

  const [tree,    setTree]    = useState([]);
  const [totals,  setTotals]  = useState({ modules: 0, subModules: 0, screens: 0 });
  /* tierEnabled[tier] = { modules: Set<string>, subModules: Set<string>, screens: Set<string> } */
  const [tierEnabled, setTierEnabled] = useState({});
  const [dirty,   setDirty]   = useState({});   // tier → bool
  const [saving,  setSaving]  = useState({});   // tier → bool
  const [loading, setLoading] = useState(true);

  /* Baseline = immutable snapshot of last saved/loaded state for diff computation.
   * Using useState (not useRef) so that React's useMemo reliably recomputes
   * diffCount when baseline advances after a save. */
  const [baseline, setBaseline] = useState({});

  const [selectedTier,     setSelectedTier]     = useState('');
  const [expanded,         setExpanded]         = useState({ modules: new Set(), subModules: new Set() });
  const [search,           setSearch]           = useState('');
  const [industries,       setIndustries]       = useState([]);
  const [selectedIndustry, setSelectedIndustry] = useState('SHARED');

  /**
   * targetIndustries — which industry codes receive the saved diff.
   * Default: [selectedIndustry]. User can add more for cross-industry updates.
   */
  const [targetIndustries, setTargetIndustries] = useState(['SHARED']);

  const toggleTarget = useCallback((code) => {
    setTargetIndustries(prev =>
      prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]
    );
  }, []);

  // Sync target when scope changes — always include the viewed scope as default
  useEffect(() => {
    setTargetIndustries([selectedIndustry || 'SHARED']);
  }, [selectedIndustry]);

  // Auto-select first tier once the list loads
  useEffect(() => {
    if (tiers.length > 0 && !selectedTier) setSelectedTier(tiers[0]);
  }, [tiers, selectedTier]);

  /* ── load tree + all-tier privileges in parallel ─────────────────────── */
  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const ic = selectedIndustry || 'SHARED';

      const treeRes  = await apiFetch(`${API}/uams/tree?industryCode=${encodeURIComponent(ic)}`);
      const treeData = await treeRes.json();
      if (treeData.error) { erpToast.error(`Tree load failed: ${treeData.error}`); setLoading(false); return; }
      setTree(treeData.tree || []);
      setTotals(treeData.totals || {});

      const results = await Promise.all(
        tiers.map(tier =>
          apiFetch(`${API}/license/tier-privileges/${encodeURIComponent(tier)}?industryCode=${encodeURIComponent(ic)}`)
            .then(r => r.json())
            .then(rows => ({ tier, rows: rows || [] }))
            .catch(() => ({ tier, rows: [] }))
        )
      );

      const te = {};
      results.forEach(({ tier, rows }) => {
        const m = new Set(), s = new Set(), sc = new Set();
        rows.forEach(p => {
          if (!p.Enabled) return;
          if (p.EntityType === 'Module')    m.add(String(p.EntityId));
          if (p.EntityType === 'SubModule') s.add(String(p.EntityId));
          if (p.EntityType === 'Screen')    sc.add(String(p.EntityId));
        });
        te[tier] = { modules: m, subModules: s, screens: sc };
      });
      setTierEnabled(te);
      // Snapshot baseline via setState so React can detect when it changes
      const newBaseline = {};
      Object.keys(te).forEach(t => { newBaseline[t] = cloneTierSets(te[t]); });
      setBaseline(newBaseline);
      setDirty({});
    } catch (e) {
      erpToast.error(`Load failed: ${e.message}`);
    }
    setLoading(false);
  }, [selectedIndustry, tiers]);

  /* load industry list once */
  useEffect(() => {
    apiFetch(`${API}/uams/industries?activeOnly=true`)
      .then(r => r.json())
      .then(d => {
        const rows = Array.isArray(d) ? d : (d.data || d.industries || []);
        setIndustries(rows.filter(r => r.IndustryCode && r.IndustryCode !== 'SHARED'));
      })
      .catch(() => {});
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  /* ── save one tier — sends ONLY the changed rows (diff), not the full state ── */
  const saveTier = useCallback(async (tierName) => {
    setSaving(prev => ({ ...prev, [tierName]: true }));

    const baselineForTier = baseline[tierName] || { modules: new Set(), subModules: new Set(), screens: new Set() };
    const current         = tierEnabled[tierName] || { modules: new Set(), subModules: new Set(), screens: new Set() };
    const changes         = computeDiff(baselineForTier, current);

    if (changes.length === 0) {
      erpToast.info(`${tierName} — no changes detected`);
      setDirty(prev => ({ ...prev, [tierName]: false }));
      setSaving(prev => ({ ...prev, [tierName]: false }));
      return;
    }

    const industryCodes = targetIndustries.length > 0 ? targetIndustries : [selectedIndustry || 'SHARED'];

    try {
      const r = await apiFetch(
        `${API}/license/tier-privileges/${encodeURIComponent(tierName)}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ changes, industryCodes }),
        }
      );
      const d = await r.json();
      if (d.error) {
        erpToast.error(`Save failed: ${d.error}`);
      } else {
        const scopeLabel = industryCodes.length > 1 ? `${industryCodes.length} industries` : industryCodes[0];
        erpToast.success(`${tierName} saved · ${changes.length} change(s) · ${d.rowsChanged ?? 0} DB rows · [${scopeLabel}]`);
        setDirty(prev => ({ ...prev, [tierName]: false }));
        // Advance baseline via setState — triggers useMemo recompute, guaranteed diffCount = 0
        setBaseline(prev => ({ ...prev, [tierName]: cloneTierSets(current) }));
      }
    } catch (e) { erpToast.error(`Save failed: ${e.message}`); }
    setSaving(prev => ({ ...prev, [tierName]: false }));
  }, [selectedIndustry, tierEnabled, targetIndustries, baseline]);

  const saveAll = async () => {
    const ds = tiers.filter(t => (tierStats[t]?.diffCount || 0) > 0 && !t.toLowerCase().includes('unlimited'));
    await Promise.all(ds.map(saveTier));
  };

  /* ── toggle helpers ──────────────────────────────────────────────────── */
  const isUnlimited = (t) => t.toLowerCase().includes('unlimited');

  const mkDirty = (tier) => setDirty(prev => ({ ...prev, [tier]: true }));

  const toggleScreen = (tier, screenId) => {
    if (isUnlimited(tier)) return;
    mkDirty(tier);
    setTierEnabled(prev => {
      const e = cloneTierSets(prev[tier]);
      const sid = String(screenId);
      if (e.screens.has(sid)) e.screens.delete(sid); else e.screens.add(sid);
      return { ...prev, [tier]: e };
    });
  };

  const toggleSubModule = (tier, sub) => {
    if (isUnlimited(tier)) return;
    mkDirty(tier);
    const sid     = String(sub.SubModuleId);
    const scrIds  = (sub.screens || []).map(s => String(s.SeqId));
    setTierEnabled(prev => {
      const e = cloneTierSets(prev[tier]);
      if (e.subModules.has(sid)) {
        e.subModules.delete(sid);
        scrIds.forEach(id => e.screens.delete(id));
      } else {
        e.subModules.add(sid);
        scrIds.forEach(id => e.screens.add(id));
      }
      return { ...prev, [tier]: e };
    });
  };

  const toggleModule = (tier, mod) => {
    if (isUnlimited(tier)) return;
    mkDirty(tier);
    const mid     = String(mod.ModuleId);
    const subIds  = (mod.subModules || []).map(s => String(s.SubModuleId));
    const scrIds  = (mod.subModules || []).flatMap(s => (s.screens || []).map(sc => String(sc.SeqId)));
    setTierEnabled(prev => {
      const e = cloneTierSets(prev[tier]);
      if (e.modules.has(mid)) {
        e.modules.delete(mid);
        subIds.forEach(id => e.subModules.delete(id));
        scrIds.forEach(id => e.screens.delete(id));
      } else {
        e.modules.add(mid);
        subIds.forEach(id => e.subModules.add(id));
        scrIds.forEach(id => e.screens.add(id));
      }
      return { ...prev, [tier]: e };
    });
  };

  const selectAllTier = (tier) => {
    if (isUnlimited(tier)) return;
    mkDirty(tier);
    const m = new Set(), s = new Set(), sc = new Set();
    tree.forEach(mod => {
      m.add(String(mod.ModuleId));
      (mod.subModules || []).forEach(sub => {
        s.add(String(sub.SubModuleId));
        (sub.screens || []).forEach(scr => sc.add(String(scr.SeqId)));
      });
    });
    setTierEnabled(prev => ({ ...prev, [tier]: { modules: m, subModules: s, screens: sc } }));
  };

  const clearAllTier = (tier) => {
    if (isUnlimited(tier)) return;
    mkDirty(tier);
    setTierEnabled(prev => ({ ...prev, [tier]: { modules: new Set(), subModules: new Set(), screens: new Set() } }));
  };

  /* ── expand / collapse ───────────────────────────────────────────────── */
  const toggleExpanded = (key, id) => {
    setExpanded(prev => {
      const s = new Set(prev[key]);
      if (s.has(String(id))) s.delete(String(id)); else s.add(String(id));
      return { ...prev, [key]: s };
    });
  };
  const expandAll = () => {
    const mods = new Set(tree.map(m => String(m.ModuleId)));
    const subs = new Set(tree.flatMap(m => (m.subModules || []).map(s => String(s.SubModuleId))));
    setExpanded({ modules: mods, subModules: subs });
  };
  const collapseAll = () => setExpanded({ modules: new Set(), subModules: new Set() });

  /* ── tri-state helpers ───────────────────────────────────────────────── */
  const moduleState = useCallback((tier, mod) => {
    if (isUnlimited(tier)) return 'all';
    const te = tierEnabled[tier];
    if (!te) return 'none';
    const all = (mod.subModules || []).flatMap(s => s.screens || []);
    if (all.length === 0) return te.modules.has(String(mod.ModuleId)) ? 'all' : 'none';
    const en = all.filter(s => te.screens.has(String(s.SeqId))).length;
    if (en === 0) return 'none';
    if (en === all.length) return 'all';
    return 'partial';
  }, [tierEnabled]);

  const subModState = useCallback((tier, sub) => {
    if (isUnlimited(tier)) return 'all';
    const te = tierEnabled[tier];
    if (!te) return 'none';
    const screens = sub.screens || [];
    if (screens.length === 0) return te.subModules.has(String(sub.SubModuleId)) ? 'all' : 'none';
    const en = screens.filter(s => te.screens.has(String(s.SeqId))).length;
    if (en === 0) return 'none';
    if (en === screens.length) return 'all';
    return 'partial';
  }, [tierEnabled]);

  const scrEnabled = useCallback((tier, seqId) => {
    if (isUnlimited(tier)) return true;
    return tierEnabled[tier]?.screens?.has(String(seqId)) || false;
  }, [tierEnabled]);

  /* ── Common column helpers (enable entity across ALL non-unlimited tiers) ── */
  const nonUnlimitedTiers = useMemo(() => tiers.filter(t => !isUnlimited(t)), [tiers]);

  const commonModState = useCallback((mod) => {
    if (nonUnlimitedTiers.length === 0) return 'none';
    const states = nonUnlimitedTiers.map(t => moduleState(t, mod));
    if (states.every(s => s === 'all'))  return 'all';
    if (states.some(s => s !== 'none')) return 'partial';
    return 'none';
  }, [nonUnlimitedTiers, moduleState]);

  const commonSubState = useCallback((sub) => {
    if (nonUnlimitedTiers.length === 0) return 'none';
    const states = nonUnlimitedTiers.map(t => subModState(t, sub));
    if (states.every(s => s === 'all'))  return 'all';
    if (states.some(s => s !== 'none')) return 'partial';
    return 'none';
  }, [nonUnlimitedTiers, subModState]);

  const commonScrEnabled = useCallback((seqId) => {
    if (nonUnlimitedTiers.length === 0) return false;
    return nonUnlimitedTiers.every(t => scrEnabled(t, seqId));
  }, [nonUnlimitedTiers, scrEnabled]);

  const toggleCommonModule = useCallback((mod) => {
    const allCommon = commonModState(mod) === 'all';
    const mid    = String(mod.ModuleId);
    const subIds = (mod.subModules || []).map(s => String(s.SubModuleId));
    const scrIds = (mod.subModules || []).flatMap(s => (s.screens || []).map(sc => String(sc.SeqId)));
    setTierEnabled(prev => {
      const next = { ...prev };
      nonUnlimitedTiers.forEach(tier => {
        const e = cloneTierSets(prev[tier]);
        if (allCommon) { e.modules.delete(mid); subIds.forEach(id => e.subModules.delete(id)); scrIds.forEach(id => e.screens.delete(id)); }
        else           { e.modules.add(mid);    subIds.forEach(id => e.subModules.add(id));    scrIds.forEach(id => e.screens.add(id));    }
        next[tier] = e;
      });
      return next;
    });
    nonUnlimitedTiers.forEach(tier => mkDirty(tier));
  }, [nonUnlimitedTiers, commonModState, mkDirty]);

  const toggleCommonSubModule = useCallback((sub) => {
    const allCommon = commonSubState(sub) === 'all';
    const sid    = String(sub.SubModuleId);
    const scrIds = (sub.screens || []).map(sc => String(sc.SeqId));
    setTierEnabled(prev => {
      const next = { ...prev };
      nonUnlimitedTiers.forEach(tier => {
        const e = cloneTierSets(prev[tier]);
        if (allCommon) { e.subModules.delete(sid); scrIds.forEach(id => e.screens.delete(id)); }
        else           { e.subModules.add(sid);    scrIds.forEach(id => e.screens.add(id));    }
        next[tier] = e;
      });
      return next;
    });
    nonUnlimitedTiers.forEach(tier => mkDirty(tier));
  }, [nonUnlimitedTiers, commonSubState, mkDirty]);

  const toggleCommonScreen = useCallback((seqId) => {
    const allCommon = commonScrEnabled(seqId);
    const sid = String(seqId);
    setTierEnabled(prev => {
      const next = { ...prev };
      nonUnlimitedTiers.forEach(tier => {
        const e = cloneTierSets(prev[tier]);
        if (allCommon) e.screens.delete(sid);
        else           e.screens.add(sid);
        next[tier] = e;
      });
      return next;
    });
    nonUnlimitedTiers.forEach(tier => mkDirty(tier));
  }, [nonUnlimitedTiers, commonScrEnabled, mkDirty]);

  /* ── per-tier stats (including diff count for save button label) ────── */
  const tierStats = useMemo(() => {
    const s = {};
    tiers.forEach(t => {
      const isUlt = t.toLowerCase().includes('unlimited');
      const diffCount = isUlt ? 0 : computeDiff(
        baseline[t] || { modules: new Set(), subModules: new Set(), screens: new Set() },
        tierEnabled[t] || { modules: new Set(), subModules: new Set(), screens: new Set() }
      ).length;
      s[t] = {
        screens:   isUlt ? totals.screens : (tierEnabled[t]?.screens?.size || 0),
        dirty:     dirty[t]  || false,
        saving:    saving[t] || false,
        diffCount,
      };
    });
    return s;
  }, [tiers, tierEnabled, baseline, dirty, saving, totals]);

  /* ── search filter ──────────────────────────────────────────────────── */
  const filteredTree = useMemo(() => {
    if (!search) return tree;
    const needle = search.toLowerCase();
    return tree.map(mod => {
      const subs = (mod.subModules || []).map(sub => {
        const screens = (sub.screens || []).filter(s =>
          (s.ScreenTitle || '').toLowerCase().includes(needle) ||
          (s.ScreenMenuName || '').toLowerCase().includes(needle)
        );
        if ((sub.SubModuleName || '').toLowerCase().includes(needle) || screens.length > 0) return { ...sub, screens };
        return null;
      }).filter(Boolean);
      if ((mod.ModuleName || '').toLowerCase().includes(needle) || subs.length > 0) return { ...mod, subModules: subs };
      return null;
    }).filter(Boolean);
  }, [tree, search]);

  const dirtyCount = tiers.filter(t => (tierStats[t]?.diffCount || 0) > 0 && !t.toLowerCase().includes('unlimited')).length;

  /* ── render ──────────────────────────────────────────────────────────── */
  return (
    <div className="tier-matrix-wrapper" style={{
      display: 'flex', height: '100%', minHeight: 600,
      background: 'var(--surface-1, #fff)',
      borderRadius: 8, border: '1px solid var(--border, #e2e8f0)',
      overflow: 'hidden',
    }}>

      {/* ─── LEFT PANE ─────────────────────────────────────────────────── */}
      <div className="tier-matrix-left-pane" style={{
        width: 268, flexShrink: 0,
        borderRight: '1px solid var(--border, #e2e8f0)',
        display: 'flex', flexDirection: 'column',
        background: 'var(--surface-2, #f8fafc)',
      }}>
        {/* header */}
        <div style={{ padding: '14px 16px 10px', borderBottom: '1px solid var(--border, #e2e8f0)' }}>
          <div style={{ fontSize: '0.79rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 2 }}>
            License Tiers
          </div>
          <div style={{ fontSize: '0.75rem', color: '#64748b', lineHeight: 1.5 }}>
            Click a tier to highlight its column
          </div>
        </div>

        {/* ── INDUSTRY SCOPE — top priority, above tier list ── */}
        <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--border, #e2e8f0)', background: '#eff6ff' }}>
          <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#2563eb', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 5 }}>
            View Scope
          </div>
          <select
            className="form-input"
            value={selectedIndustry}
            onChange={e => setSelectedIndustry(e.target.value)}
            data-testid="tier-matrix-industry"
            style={{ width: '100%', fontSize: '0.79rem', borderColor: '#bfdbfe' }}
          >
            <option value="SHARED">— Shared baseline —</option>
            {industries.map(i => (
              <option key={i.IndustryCode} value={i.IndustryCode}>{i.IndustryName} ({i.IndustryCode})</option>
            ))}
          </select>
        </div>

        {/* ── APPLY CHANGES TO — multi-industry target for saves ── */}
        <div style={{ padding: '10px 12px', borderBottom: '2px solid var(--border, #e2e8f0)', background: '#f0fdf4' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 5 }}>
            <Layers size={11} color="#16a34a" />
            <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#16a34a', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
              Apply Changes To
            </span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 110, overflowY: 'auto' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.79rem', cursor: 'pointer', padding: '2px 0' }}>
              <input
                type="checkbox"
                checked={targetIndustries.includes('SHARED')}
                onChange={() => toggleTarget('SHARED')}
                data-testid="tier-matrix-target-SHARED"
              />
              <span style={{ fontWeight: targetIndustries.includes('SHARED') ? 700 : 400 }}>SHARED (baseline)</span>
            </label>
            {industries.map(i => (
              <label key={i.IndustryCode} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.79rem', cursor: 'pointer', padding: '2px 0' }}>
                <input
                  type="checkbox"
                  checked={targetIndustries.includes(i.IndustryCode)}
                  onChange={() => toggleTarget(i.IndustryCode)}
                  data-testid={`tier-matrix-target-${i.IndustryCode}`}
                />
                <span style={{ fontWeight: targetIndustries.includes(i.IndustryCode) ? 700 : 400 }}>{i.IndustryName}</span>
              </label>
            ))}
          </div>
          {targetIndustries.length > 1 && (
            <div style={{ marginTop: 5, fontSize: '0.72rem', color: '#16a34a', background: '#dcfce7', borderRadius: 4, padding: '2px 6px', display: 'inline-block' }}>
              Saves will apply to {targetIndustries.length} industries
            </div>
          )}
        </div>

        {/* tier cards */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px' }}>
          {tiers.map(tier => {
            const color   = colors[tier] || '#64748b';
            const stats   = tierStats[tier];
            const isSel   = selectedTier === tier;
            const isUlt   = isUnlimited(tier);
            return (
              /* Use <div role="button"> — outer <button> is invalid HTML when it wraps inner <button> elements */
              <div
                key={tier}
                role="button"
                tabIndex={0}
                onClick={() => setSelectedTier(tier)}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') setSelectedTier(tier); }}
                data-testid={`tier-matrix-sel-${tier.replace(/\s+/g, '-')}`}
                style={{
                  width: '100%', textAlign: 'left', cursor: 'pointer',
                  padding: '10px 12px', borderRadius: 8, marginBottom: 4,
                  background: isSel ? `${color}18` : 'transparent',
                  borderLeft: `3px solid ${isSel ? color : 'transparent'}`,
                  transition: 'all 0.15s',
                  outline: 'none',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 3 }}>
                  <span style={{ fontWeight: 700, fontSize: '0.86rem', color: isSel ? color : 'var(--text-primary, #0f172a)' }}>{tier}</span>
                  {stats.diffCount > 0 && (
                    <span style={{ fontSize: '0.72rem', background: '#fef3c7', color: '#d97706', padding: '1px 5px', borderRadius: 3, fontWeight: 800 }}>
                      {stats.diffCount} change{stats.diffCount > 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                  {isUlt
                    ? <span style={{ color: '#8b5cf6', fontWeight: 700 }}>All screens</span>
                    : `${stats.screens} / ${totals.screens} screens`
                  }
                </div>

                {/* inline actions — only for selected tier */}
                {isSel && !isUlt && (
                  <div style={{ display: 'flex', gap: 4, marginTop: 8 }}>
                    <button
                      onClick={e => { e.stopPropagation(); selectAllTier(tier); }}
                      style={{ flex: 1, fontSize: '0.75rem', padding: '3px 0', border: `1px solid ${color}50`, borderRadius: 4, background: `${color}12`, color, cursor: 'pointer', fontWeight: 700 }}
                    >ALL</button>
                    <button
                      onClick={e => { e.stopPropagation(); clearAllTier(tier); }}
                      style={{ flex: 1, fontSize: '0.75rem', padding: '3px 0', border: '1px solid #e2e8f0', borderRadius: 4, background: '#fff', color: '#64748b', cursor: 'pointer', fontWeight: 700 }}
                    >NONE</button>
                    <button
                      onClick={e => { e.stopPropagation(); if (stats.diffCount > 0) saveTier(tier); }}
                      disabled={stats.diffCount === 0 || stats.saving}
                      data-testid={`tier-matrix-save-${tier.replace(/\s+/g, '-')}`}
                      style={{
                        flex: 1.5, fontSize: '0.75rem', padding: '3px 0', borderRadius: 4,
                        cursor: stats.diffCount > 0 ? 'pointer' : 'default', fontWeight: 700, border: 'none',
                        background: stats.diffCount > 0 ? color : '#f1f5f9',
                        color: stats.diffCount > 0 ? '#fff' : '#94a3b8',
                        opacity: stats.saving ? 0.65 : 1,
                      }}
                    >{stats.saving ? '…' : stats.diffCount > 0 ? `SAVE (${stats.diffCount})` : 'SAVED'}</button>
                  </div>
                )}

                {isSel && isUlt && (
                  <div style={{ marginTop: 6, fontSize: '0.72rem', color: '#8b5cf6', fontStyle: 'italic' }}>
                    Auto-enabled · no save required
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* save-all strip */}
        {dirtyCount > 0 && (
          <div style={{ padding: '10px 12px', borderTop: '1px solid #fde68a', background: '#fffbeb' }}>
            <div style={{ fontSize: '0.72rem', color: '#92400e', marginBottom: 4 }}>
              {targetIndustries.length > 1
                ? `Will apply to: ${targetIndustries.join(', ')}`
                : `Scope: ${targetIndustries[0] || 'SHARED'}`}
            </div>
            <button
              className="erp-btn erp-btn-primary"
              onClick={saveAll}
              style={{ width: '100%', fontSize: 11 }}
              data-testid="tier-matrix-save-all"
            >
              <Save size={11} /> Save All ({dirtyCount} tier{dirtyCount > 1 ? 's' : ''} with changes)
            </button>
          </div>
        )}
      </div>

      {/* ─── RIGHT PANE ────────────────────────────────────────────────── */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {/* toolbar */}
        <div style={{
          padding: '10px 14px', borderBottom: '1px solid var(--border, #e2e8f0)',
          display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
          background: 'var(--surface-2, #f8fafc)', flexShrink: 0,
        }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
            <Search size={13} color="#94a3b8" style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)' }} />
            <input
              className="form-input"
              placeholder="Search modules / screens…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ paddingLeft: 30, fontSize: 11 }}
              data-testid="tier-matrix-search"
            />
          </div>
          <button className="erp-btn" onClick={expandAll}   style={{ fontSize: '0.79rem' }} data-testid="tier-matrix-expand">
            <ChevronDown size={11} /> Expand All
          </button>
          <button className="erp-btn" onClick={collapseAll} style={{ fontSize: '0.79rem' }} data-testid="tier-matrix-collapse">
            <ChevronRight size={11} /> Collapse All
          </button>
          <button className="erp-btn" onClick={loadAll}     style={{ fontSize: '0.79rem' }} data-testid="tier-matrix-refresh">
            <RefreshCw size={11} /> Refresh
          </button>
          <div style={{ marginLeft: 'auto', fontSize: '0.79rem', color: '#64748b', background: '#f1f5f9', padding: '4px 10px', borderRadius: 6, whiteSpace: 'nowrap' }}>
            {totals.modules}M · {totals.subModules}S · {totals.screens}Sc
          </div>
        </div>

        {/* info banner */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '6px 14px',
          background: '#eff6ff', borderBottom: '1px solid #bfdbfe', flexShrink: 0,
          fontSize: '0.79rem', color: '#1e40af',
        }}>
          <Database size={11} />
          <span>Each column = one License Tier. Checkboxes control which screens that tier can access. <strong>Highlighted column</strong> = active tier selected in left panel.</span>
        </div>

        {/* matrix */}
        {loading ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
            <div style={{ textAlign: 'center' }}>
              <Database size={24} color="#3b82f6" style={{ animation: 'tmSpin 1s linear infinite', marginBottom: 10 }} />
              <div style={{ fontSize: 11 }}>Loading privilege matrix across all tiers…</div>
            </div>
          </div>
        ) : (
          <div style={{ flex: 1, overflow: 'auto' }}>
            <table style={{
              borderCollapse: 'collapse',
              minWidth: `${230 + (tiers.length + 1) * COL_W}px`,
              width: '100%',
            }}>
              <colgroup>
                <col style={{ width: 220, minWidth: 160 }} />
                <col style={{ width: COL_W }} />{/* Common */}
                {tiers.map(t => <col key={t} style={{ width: COL_W }} />)}
              </colgroup>

              {/* ── sticky header row ─────────────────────────────────── */}
              <thead>
                <tr style={{ height: HDR_H }}>
                  <th style={{
                    position: 'sticky', top: 0, left: 0, zIndex: 10,
                    background: 'var(--surface-2, #f8fafc)',
                    borderBottom: '2px solid var(--border, #e2e8f0)',
                    borderRight: '1px solid var(--border, #e2e8f0)',
                    padding: '10px 14px', textAlign: 'left', verticalAlign: 'bottom',
                  }}>
                    <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                      Module / Screen
                    </div>
                  </th>

                  {/* Common column header */}
                  <th
                    title="Common — enable this entity across ALL tiers simultaneously"
                    style={{
                      position: 'sticky', top: 0, zIndex: 5,
                      width: COL_W, height: HDR_H, padding: '6px 0 8px',
                      background: '#e0f2fe',
                      borderBottom: '2px solid #0ea5e9',
                      borderLeft: '2px solid #bae6fd',
                      borderRight: '1px solid #bae6fd',
                      verticalAlign: 'bottom',
                    }}
                    data-testid="tier-matrix-col-common"
                  >
                    <div style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      writingMode: 'vertical-rl', transform: 'rotate(180deg)',
                      fontSize: '0.79rem', fontWeight: 700, color: '#0369a1',
                      letterSpacing: '0.04em', height: '100%', gap: 4,
                    }}>
                      <Globe size={11} style={{ writingMode: 'horizontal-tb', transform: 'none', marginBottom: 2 }} />
                      Common
                    </div>
                    <div style={{ width: '80%', height: 3, borderRadius: 2, background: '#0ea5e9', margin: '0 auto' }} />
                  </th>

                  {tiers.map(tier => {
                    const color = colors[tier] || '#64748b';
                    const isSel = selectedTier === tier;
                    return (
                      <th
                        key={tier}
                        onClick={() => setSelectedTier(tier)}
                        style={{
                          position: 'sticky', top: 0, zIndex: 5,
                          width: COL_W, height: HDR_H, padding: '6px 0 8px',
                          background: isSel ? `${color}14` : 'var(--surface-2, #f8fafc)',
                          borderBottom: `2px solid ${isSel ? color : '#e2e8f0'}`,
                          borderLeft: '1px solid var(--border, #e2e8f0)',
                          cursor: 'pointer', transition: 'background 0.15s',
                          verticalAlign: 'bottom',
                        }}
                        title={tier}
                        data-testid={`tier-matrix-col-${tier.replace(/\s+/g, '-')}`}
                      >
                        <div style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          writingMode: 'vertical-rl', transform: 'rotate(180deg)',
                          fontSize: '0.79rem', fontWeight: 700,
                          color: isSel ? color : '#64748b',
                          letterSpacing: '0.04em', height: '100%',
                        }}>
                          {shorts[tier] || tier.split(' ')[0]}
                        </div>
                        {isSel && (
                          <div style={{
                            width: '80%', height: 3, borderRadius: 2,
                            background: color, margin: '0 auto',
                          }} />
                        )}
                      </th>
                    );
                  })}
                </tr>
              </thead>

              {/* ── body ──────────────────────────────────────────────── */}
              <tbody>
                {filteredTree.length === 0 ? (
                  <tr>
                    <td colSpan={tiers.length + 1} style={{ padding: 48, textAlign: 'center', color: '#94a3b8', fontSize: 12 }}>
                      No modules found. Add modules via the <strong>UAMS Master</strong> tab.
                    </td>
                  </tr>
                ) : filteredTree.map(mod => (
                  <ModuleBlock
                    key={mod.ModuleId}
                    mod={mod}
                    selectedTier={selectedTier}
                    expanded={expanded}
                    onToggleExpand={toggleExpanded}
                    onToggleModule={toggleModule}
                    onToggleSubModule={toggleSubModule}
                    onToggleScreen={toggleScreen}
                    moduleState={moduleState}
                    subModState={subModState}
                    scrEnabled={scrEnabled}
                    tiers={tiers}
                    colors={colors}
                    commonModState={commonModState}
                    commonSubState={commonSubState}
                    commonScrEnabled={commonScrEnabled}
                    onToggleCommonModule={toggleCommonModule}
                    onToggleCommonSubModule={toggleCommonSubModule}
                    onToggleCommonScreen={toggleCommonScreen}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <style>{`
        @keyframes tmSpin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
